import { Cth01CircuitGroups, Cth01CtChannels } from 'src/types/Cth01CircuitGroups';
import { AxiosError } from 'axios';
import { useQuery, UseQueryOptions, UseQueryResult, useMutation, useQueryClient } from 'react-query';
import { useAggregatedQueries } from 'src/hooks/useAggregatedQuery';
import { Device } from 'src/types/Device';
import { PowerSample, Sample } from 'src/types/Sample';
import { SigfoxTokenState } from 'src/types/SigfoxTokenState';
import { get, post, put, RequestBody } from '../utils';
import { isPage, Page, PageRequest } from '../pagination';
import { getPropertyInfo } from './legacy-routes';
import { RoomRadarDetails } from 'src/components/device/DeviceRoomRadar';
import { Room, RoomLocation } from 'src/types/Room';
import TetherApi from '../TetherApi';
import { PropertyInfo } from 'src/types/PropertyInfo';
import { getPropertyById, updateRoomLocation, PropertyQueryKeys } from './property';
import { Property } from 'src/types/Property';
import { useMemo } from 'react';

export enum QueryKeys {
  Device = 'Device',
  DeviceSample = 'DeviceSample',
  NetworkEvents = 'NetworkEvents',
  RoomRadarDetails = 'RoomRadarDetails',
  Room = 'Room',
  RoomLocation = 'RoomLocation',
  PropertyInfo = 'PropertyInfo',
  Property = 'Property',
  Circuit = 'Circuit',
  DeviceConfiguration = 'DeviceConfiguration',
  BluetoothPassword = 'BluetoothPassword',
}

export function getSigfoxTokenState(deviceId: string): Promise<SigfoxTokenState> {
  return get<SigfoxTokenState>(`device/${deviceId}/sigfoxtokenstate`);
}

export async function getDevice(deviceId: string, options?: { retry: false }): Promise<Device> {
  return new Device(await get<Device>(`device/${deviceId}`, undefined, options?.retry === false ? 3 : 0));
}

export const useGetDevicesById = (deviceIds: string[]) => {
  return useAggregatedQueries<Device>(
    deviceIds.map((deviceId) => ({
      queryKey: [QueryKeys.Device, deviceId],
      queryFn: () => {
        return getDevice(deviceId);
      },
    }))
  );
};

export async function createDevice(device: any): Promise<Device> {
  return post<Device>('device', device);
}

export async function updateDevice(device: Device): Promise<Device> {
  return put<Device>(`device/${device.id}`, (device as unknown) as RequestBody);
}

export async function getDevices(): Promise<Device[]> {
  return (await get<Device[]>('device')).map((d) => new Device(d));
}

/**
 * EVERY device of a type, walked page by page.
 *
 * Almost never the right call, and it is named to say so. A device table shows a page and
 * should ask for one ({@link useSearchDevicesByTypePage}); walking every page of ENVIROQ
 * is ~114,000 rows in the browser.
 *
 * It exists for the two GATEWAY screens that genuinely operate on the whole set — the
 * recon report classifies every gateway against the network, and there is no page of that
 * — and GATEWAY is a few hundred rows rather than a device type that grows with sales.
 *
 * Bounded, and the bound is REPORTED: `truncated` says the answer is a prefix, so a caller
 * can tell the user rather than quietly showing part of a set as if it were all of it.
 * That is the failure this replaced — a single unpaged call that took the server's default
 * fifty gateways and looked exactly like the complete list.
 */
export const DEVICE_TYPE_WALK_CAP = 2000;

export interface DevicesOfTypeResult {
  devices: Device[];
  /** True when the cap stopped the walk, so `devices` is a prefix of the type. */
  truncated: boolean;
}

export async function getAllDevicesOfType(
  type: string,
  filters: { customerId?: string } = {}
): Promise<DevicesOfTypeResult> {
  const devices: Device[] = [];
  const limit = 200;

  for (let offset = 0; offset < DEVICE_TYPE_WALK_CAP; offset += limit) {
    const page = await fetchDevicePage({ type, ...filters }, { limit, offset, sort: 'id' });
    devices.push(...page.devices);

    if (page.devices.length < limit) {
      return { devices, truncated: false };
    }

    // With the envelope the walk knows exactly where it ends rather than inferring it
    // from a short page; without it (flag off) the short page above is the only signal.
    if (page.total >= 0 && devices.length >= page.total) {
      return { devices, truncated: false };
    }
  }

  // ONE row past the cap, to tell "there are more" from "there are exactly this many".
  // Reporting the tenth full page as truncation warns an operator about gateways that are
  // not missing — and the recon page's banner says "more than 2,000 exist".
  //
  // Its failure must not discard the walk. The probe refines a WARNING; the rows behind it
  // are the answer, already fetched, and rejecting here threw away ten successful pages
  // because an eleventh one-row request timed out. `true` is the conservative reading: the
  // walk did run to the cap, so a prefix is exactly what the caller has.
  try {
    const beyond = await fetchDevicePage({ type, ...filters }, { limit: 1, offset: DEVICE_TYPE_WALK_CAP, sort: 'id' });
    return { devices, truncated: beyond.devices.length > 0 };
  } catch {
    return { devices, truncated: true };
  }
}

export function getDeviceSamples(deviceId: string, fromTimestamp: string, toTimestamp: string): Promise<Sample[]> {
  const query = {
    fromTimestamp,
    toTimestamp,
  };
  return get<Sample[]>(`device/${deviceId}/samples`, query);
}

export function getHotdropReadings(deviceId: string, fromTimestamp: string, toTimestamp: string): Promise<PowerSample[]> {
  const query = {
    startTimestamp: fromTimestamp,
    endTimestamp: toTimestamp,
  };
  return get<PowerSample[]>(`hotdrop/${deviceId}/readings`, query);
}

export type GetDeviceReadingOptions = {
  milliseconds?: boolean;
};
export type ReadingArray = [number, number][];

export type DeviceReadingResponse = {
  temperature?: ReadingArray;
  humidity?: ReadingArray;
  co2?: ReadingArray;
  pressure?: ReadingArray;
  battery?: ReadingArray;
  light?: ReadingArray;
  dewPoint?: ReadingArray;
  pm1?: ReadingArray;
  pm2_5?: ReadingArray;
  pm10?: ReadingArray;
  flag_co2Calibration?: ReadingArray;
  flag_unknownDeviceType?: ReadingArray;
  totalkWh?: ReadingArray;
  maxCurrent?: ReadingArray;
  minCurrent?: ReadingArray;
  cumulativeActivityCount?: ReadingArray;
  occupied?: ReadingArray;
  value?: ReadingArray;
  water?: ReadingArray;
  maxDwellTime?: ReadingArray;
  count?: ReadingArray;
  averageDwellTime?: ReadingArray;
  payload?: ReadingArray;
};

export async function getDeviceReadings(
  deviceId: string,
  fromTimestamp: string,
  toTimestamp: string,
  opts?: GetDeviceReadingOptions
): Promise<DeviceReadingResponse> {
  const query = {
    fromTimestamp,
    toTimestamp,
    ...opts,
  };

  const readings = await get<DeviceReadingResponse>(`device/${deviceId}/readings`, query);

  // milliseconds isn't on the server yet - so manually do it
  if (query?.milliseconds) {
    Object.keys(readings).forEach((key) => {
      const arr = readings[key] as ReadingArray;

      arr.forEach((pair) => {
        pair[0] *= 1000;
      });
    });
  }

  return readings;
}

export async function getLatestDeviceSample(deviceId: string): Promise<Sample> {
  try {
    const result = await get<Sample>(`device/${deviceId}/samples/latest`);
    return result;
  } catch (_err) {
    return get<Sample>(`device/${deviceId}/latest`);
  }
}

export function useGetLatestDeviceSample(deviceId: string, options?: UseQueryOptions<Sample, AxiosError>) {
  return useQuery([QueryKeys.Device, deviceId, 'latestSample'], () => getLatestDeviceSample(deviceId), options);
}

export function useQueryDevicesByIds(ids: string[], enabled?: boolean) {
  return useAggregatedQueries<Device | null, AxiosError>(
    enabled !== undefined && !enabled
      ? []
      : ids.map((id) => {
          const deviceId = id;
          return {
            queryKey: [QueryKeys.Device, deviceId],
            queryFn: async () => getDevice(deviceId),
          };
        })
  );
}

/**
 * The same devices, in batches, using the `ids[]` filter the device search already
 * supports.
 *
 * {@link useQueryDevicesByIds} issues ONE HTTP request per device. On an organisation
 * with ~7,500 devices that is ~7,500 requests fired at once from a single browser tab,
 * which is where most of the Organisation Devices tab's cost lives. Same data, ~250
 * requests instead — and the API change is zero, because `GET /device?ids=…` has existed
 * all along.
 *
 * Left alongside rather than replacing it: the per-id version populates a per-device cache
 * entry that other screens read, so switching every caller is a separate decision from
 * fixing the one screen that fans out over a whole organisation.
 */
export function useQueryDevicesByIdsBatched(ids: string[], enabled?: boolean) {
  const batches = useMemo(() => {
    if (enabled === false) return [];
    const chunks: string[][] = [];
    for (let i = 0; i < ids.length; i += DEVICE_BATCH_SIZE) {
      chunks.push(ids.slice(i, i + DEVICE_BATCH_SIZE));
    }
    return chunks;
  }, [ids, enabled]);

  const { data, ...rest } = useAggregatedQueries<Device[], AxiosError>(
    batches.map((batch) => ({
      // Keyed by the batch, not by device, so paging or reordering the source list reuses
      // whole batches rather than re-fetching everything on a one-device change.
      queryKey: [QueryKeys.Device, 'batch', batch],
      queryFn: async () => getDeviceBatch(batch),
    }))
  );

  return {
    ...rest,
    data: (data ?? []).filter(Boolean).flat(),
  };
}

export function useGetDevice(deviceId: string, options?: UseQueryOptions<Device, AxiosError>): UseQueryResult<Device, AxiosError> {
  return useQuery(
    [QueryKeys.Device, deviceId],
    async () => {
      return getDevice(deviceId);
    },
    options
  );
}

export type DeviceSignal = {
  deviceId: string;
  timestamp: string;
  rssi?: number;
  percentage?: number;

  frequency?: number;
  gatewayNetworkId?: string;
  loraBandwidth?: number;
  loraDataRate?: number;
  loraSpreadingFactor?: number;
  lrrCount?: number;
  snr?: number;
};

export function getLatestDeviceSignal(deviceId: string): Promise<DeviceSignal> {
  return get<DeviceSignal>(`device/${deviceId}/signal`);
}

export type DeviceJoinEvent = {
  deviceId: string;
  timestamp: string;
  eventType: string;
  eventId: string;
  devEui: string;
  wirelessDeviceId: string;
  isKnownTetherDevice: boolean;
};

type NetworkEventRecord = DeviceJoinEvent | DeviceSignal;

export function getNetworkEvents(deviceId: string): Promise<NetworkEventRecord[]> {
  return get<NetworkEventRecord[]>(`device/${deviceId}/network-events`);
}

export function useGetNetworkEvents(deviceId: string, options?: UseQueryOptions<NetworkEventRecord[], Error>) {
  const { data, isLoading, error } = useQuery(
    [QueryKeys.NetworkEvents, deviceId],
    () => {
      return getNetworkEvents(deviceId);
    },
    options
  );

  const networkEvents = useMemo(() => data || [], [data]);

  return {
    networkEvents,
    isLoading,
    error,
  };
}

export function useGetRoomRadarDetails(id: string, options?: UseQueryOptions<RoomRadarDetails, Error>) {
  const { data: roomRadar, isLoading, error } = useQuery(
    [QueryKeys.RoomRadarDetails, id],
    () => {
      return get<RoomRadarDetails>(`roomradar/${id}`);
    },
    options
  );

  return {
    roomRadar,
    isLoading,
    error,
  };
}

export function useGetRoomRadarConfig(id: string, options?: UseQueryOptions<RoomRadarDetails, Error>) {
  const { data: roomRadar, isLoading, error } = useQuery(
    [QueryKeys.RoomRadarDetails, id],
    () => {
      return get<RoomRadarDetails>(`roomradar/${id}`);
    },
    options
  );

  return {
    roomRadar,
    isLoading,
    error,
  };
}

export function useGetRoom(id: string, options?: UseQueryOptions<Room, Error>) {
  const { data: room, isLoading, error } = useQuery(
    [QueryKeys.Room, id],
    () => {
      return TetherApi.getRoomById(id);
    },
    options
  );

  return {
    room,
    isLoading,
    error,
  };
}

export function useGetRoomLocation(id: string, options?: UseQueryOptions<RoomLocation, Error>) {
  const { data: roomLocation, isLoading, error } = useQuery(
    [QueryKeys.RoomLocation, id],
    () => {
      return TetherApi.getRoomLocationById(id);
    },
    options
  );

  return {
    roomLocation,
    isLoading,
    error,
  };
}

export function useUpdateRoomLocation() {
  const queryClient = useQueryClient();

  return useMutation((roomLocation: Partial<RoomLocation> & { id: string }) => updateRoomLocation(roomLocation), {
    onSuccess: (data) => {
      queryClient.invalidateQueries([QueryKeys.RoomLocation, data.id]);
      queryClient.invalidateQueries(PropertyQueryKeys.RoomLocations);
      queryClient.invalidateQueries(QueryKeys.PropertyInfo);
    },
  });
}

interface HotDropProvision {
  hotdropId: string;
  networkType: 'GATEWAY' | 'SPARK';
  backendVersion: 'V2';
  firmwareVersion: 28 | 29;
  sku: string;
  loraNetworkDetails?: {
    appEUI?: string;
    appKey?: string;
  };
}

export function provisionHotdrop(hotDropProvisionSettings: HotDropProvision): Promise<Device> {
  return post<Device>('hotdrop', {
    ...hotDropProvisionSettings,
  });
}

export function useGetPropertyInfo(id: string, options?: UseQueryOptions<PropertyInfo, Error>) {
  const { data: propertyInfo, isLoading, error } = useQuery(
    [QueryKeys.PropertyInfo, id],
    () => {
      return getPropertyInfo(id);
    },
    {
      enabled: Boolean(id),
      staleTime: Infinity,
      ...options,
    }
  );

  return {
    propertyInfo,
    isLoading,
    error,
  };
}

export function useGetProperty(id: string, options?: UseQueryOptions<Property, Error>) {
  const { data: property, isLoading, error } = useQuery(
    [QueryKeys.Property, id],
    () => {
      return getPropertyById(id);
    },
    {
      enabled: Boolean(id),
      staleTime: Infinity,
      ...options,
    }
  );

  return {
    property,
    isLoading,
    error,
  };
}

export type DeviceConfiguration = {
  lineVoltage: number;
  vutilitiId: string;
  backendVersion: string;
  /** Milesight CTH01 hub only: how each CT group is wired (drives its virtual circuit children). */
  circuitGroups?: Cth01CircuitGroups;
  /** Milesight CTH01 hub only: each channel's CT range and direction, sent to the hub by "Send layout to hub". */
  ctChannels?: Cth01CtChannels;
};

async function getDeviceConfiguration(deviceId: string): Promise<DeviceConfiguration> {
  return get<DeviceConfiguration>(`device/${deviceId}/configuration`);
}

async function updateDeviceConfiguration(deviceId: string, config: Partial<DeviceConfiguration>) {
  return put(`device/${deviceId}/configuration`, config);
}

export function useGetDeviceConfiguration(deviceId: string, options?: UseQueryOptions<DeviceConfiguration, Error>) {
  return useQuery(
    [QueryKeys.DeviceConfiguration, deviceId],
    () => {
      return getDeviceConfiguration(deviceId);
    },
    options
  );
}

export function useUpdateDeviceConfiguration(deviceId: string) {
  const queryClient = useQueryClient();

  return useMutation((config: Partial<DeviceConfiguration>) => updateDeviceConfiguration(deviceId, config), {
    onSuccess: () => {
      queryClient.invalidateQueries([QueryKeys.DeviceConfiguration, deviceId]);
    },
  });
}

/** A device's Bluetooth (ToolBox) password, decrypted by the API for this read only; null when none is stored. */
export type BluetoothPassword = { password: string | null };

export function useGetBluetoothPassword(deviceId: string, options?: UseQueryOptions<BluetoothPassword, Error>) {
  return useQuery(
    [QueryKeys.BluetoothPassword, deviceId],
    () => get<BluetoothPassword>(`device/${deviceId}/bluetooth-password`),
    options
  );
}

export function useUpdateBluetoothPassword(deviceId: string) {
  const queryClient = useQueryClient();

  return useMutation((password: string) => put(`device/${deviceId}/bluetooth-password`, { password }), {
    onSuccess: () => {
      queryClient.invalidateQueries([QueryKeys.BluetoothPassword, deviceId]);
    },
  });
}

/**
 * ONE page of a device search.
 *
 * The endpoint adopted the org-scale list contract (api#), so a paged request answers with
 * `{ items, total, limit, offset }` and `total` is the number a table needs to say how
 * many rows there are and to offer a page to jump to.
 *
 * `total` is -1 only when the server genuinely did not say — ORG_SCALE_PAGINATION off, so
 * the legacy bare array came back. The array is still ONE page (the legacy path honours
 * `page`/`limit`), so the count cannot be recovered from it; a short page does pin it
 * exactly, and that is the one case worth reporting. -1 renders as "1–20 of more than 20"
 * with arrows instead of page numbers — honest about what is known rather than inventing
 * a total.
 */
export interface DevicePage {
  devices: Device[];
  /** Total matching rows on the server, or -1 when the server did not report one. */
  total: number;
  /**
   * True when the server answered with the `{ items, total }` envelope (so `total` is a server
   * count); false for the legacy bare array, where a short page's `total` is only what was seen.
   */
  serverPaged?: boolean;
}

/**
 * ONE page of `GET device`, in either response shape. Exported for the MouldWatch report,
 * whose counts need every device of a type in an organisation (see
 * pages/management/projects/mouldwatch/useMouldWatchData.ts) and which walks it with this.
 */
export async function fetchDevicePage(
  filters: Record<string, string | number | boolean | undefined>,
  request: PageRequest
): Promise<DevicePage> {
  // The endpoint pages by 1-based PAGE, not offset, so a table's offset is converted here
  // rather than every caller having to know that.
  const page = Math.floor(request.offset / request.limit) + 1;

  const response = await get<Device[] | Page<Device>>('device', {
    ...filters,
    limit: request.limit,
    page,
    // BOTH spellings, on purpose. `page` is what the endpoint's legacy path reads, so this
    // request still returns the right rows while ORG_SCALE_PAGINATION is off. `paged` is
    // what opts it into the envelope once the flag is on — the endpoint deliberately does
    // NOT opt in on `page`/`limit`/`sort`/`desc`, because it has accepted those for years
    // and flipping a global flag must not turn an array into an object under a browser tab
    // that is already open.
    paged: true,
    ...(request.search ? { search: request.search } : {}),
    ...(request.sort ? { sort: request.sort } : {}),
    ...(request.desc ? { desc: true } : {}),
  });

  if (isPage<Device>(response)) {
    return {
      devices: response.items.map((d) => new Device(d)),
      total: response.total,
      serverPaged: true,
    };
  }

  const rows = response ?? [];
  return {
    devices: rows.map((d) => new Device(d)),
    // A page that did not fill is the last one, so the total is exactly what has been
    // seen. A full page says nothing about what follows it.
    total: rows.length < request.limit ? request.offset + rows.length : -1,
    serverPaged: false,
  };
}

/** Sort fields the device search accepts. Anything else is a 400. */
export const DEVICE_SORT_FIELDS = [
  'id', 'type', 'firmwareVersion', 'hardwareVersion', 'serialNumber', 'sku', 'createdAt',
] as const;

/**
 * The three paged device searches below differ only in what scopes them. `keepPreviousData`
 * is what makes paging feel like paging: the rows stay while the next page loads, so the
 * table dims instead of emptying. The query stays idle until the scope is known, because a
 * device search with no scope is every device on the platform.
 */
function useDevicePageQuery(
  scope: string,
  scopeId: string | undefined,
  filters: Record<string, string | number | boolean | undefined>,
  request: PageRequest,
  options?: UseQueryOptions<DevicePage, AxiosError>
): UseQueryResult<DevicePage, AxiosError> {
  return useQuery<DevicePage, AxiosError>(
    [QueryKeys.Device, scope, scopeId, request],
    () => fetchDevicePage(filters, request),
    {
      keepPreviousData: true,
      ...options,
      // AFTER the spread, and ANDed rather than replaced. Spread last, a caller passing
      // `enabled: true` would overwrite the guard wholesale and the query would fire with
      // an undefined scope id — a request for every device on the platform, filtered by
      // `organisationId=undefined`.
      enabled: Boolean(scopeId) && (options?.enabled ?? true),
    }
  );
}

/**
 * One page of an organisation's devices.
 *
 * Two rounds of history: this tab first resolved ~8,900 device ids 30 at a time (600+
 * requests of nothing but uuids), then walked every page of an organisation-scoped search
 * (18 requests) and handed the whole organisation to a client-side table. The second is
 * the one worth naming — paging the transport while loading every page still puts 8,880
 * rows in the browser and still searches them there.
 */
export function useSearchDevicesForOrganisationPage(
  organisationId: string | undefined,
  request: PageRequest,
  options?: UseQueryOptions<DevicePage, AxiosError>
): UseQueryResult<DevicePage, AxiosError> {
  return useDevicePageQuery('organisation', organisationId, { organisationId }, request, options);
}

/** One page of a customer's devices. The customer sibling of the above. */
export function useSearchDevicesForCustomerPage(
  customerId: string | undefined,
  request: PageRequest,
  options?: UseQueryOptions<DevicePage, AxiosError>
): UseQueryResult<DevicePage, AxiosError> {
  return useDevicePageQuery('customer', customerId, { customerId }, request, options);
}

/**
 * One page of devices of a type — the browse-by-type view.
 *
 * `rootOnly` because a three-phase meter creates a virtual child device per phase, and the
 * browse list wants the meters. It is a FILTER rather than a post-hoc `!parentDeviceId` on
 * the rows: dropping children after the page arrives shrinks what is shown while `total`
 * still counts what the database returned, so the footer says 200 and the table shows 50.
 */
export function useSearchDevicesByTypePage(
  type: string | undefined,
  request: PageRequest,
  options?: UseQueryOptions<DevicePage, AxiosError>
): UseQueryResult<DevicePage, AxiosError> {
  return useDevicePageQuery('type', type, { type, rootOnly: true }, request, options);
}

export const DEVICE_BATCH_SIZE = 30;

export async function getDeviceBatch(ids: string[]): Promise<Device[]> {
  if (ids.length === 0) return [];
  const qs = ids.map((id) => `ids=${encodeURIComponent(id)}`).join('&');
  return (await get<Device[]>(`device?${qs}`)).map((d) => new Device(d));
}

/**
 * How many getDeviceBatch requests getDevicesInBatches keeps in flight. Each request is already
 * DEVICE_BATCH_SIZE devices (one indexed IN query plus decoration), so three is at most 90 device
 * rows at once, while a gateway that heard 300 devices still resolves in four rounds.
 */
export const DEVICE_BATCH_CONCURRENCY = 3;

export type DeviceBatchLookup = {
  /** The live devices found, in the order their ids were given. */
  devices: Device[];
  /**
   * How many requested ids are unaccounted for because their request FAILED. Ids the server
   * answered without (no live device) are not counted: absent is an answer, a failure is not.
   */
  failedCount: number;
};

/**
 * Every live device among `ids`, in the order the ids were given, looked up DEVICE_BATCH_SIZE at a
 * time with at most DEVICE_BATCH_CONCURRENCY requests in flight. Not one request per id: a caller
 * holding an arbitrary id list (a gateway's seen devices, say) must not fire one request per id.
 *
 * An id with no live device is simply absent, as a 404 from a per-id lookup was. A failed request
 * loses only its own batch, and is counted in `failedCount` so the caller can say the result is
 * incomplete rather than show a quietly shorter list.
 */
export async function getDevicesInBatches(ids: string[]): Promise<DeviceBatchLookup> {
  const batches: string[][] = [];
  for (let i = 0; i < ids.length; i += DEVICE_BATCH_SIZE) {
    batches.push(ids.slice(i, i + DEVICE_BATCH_SIZE));
  }

  const found = new Map<string, Device>();
  let failedCount = 0;
  for (let i = 0; i < batches.length; i += DEVICE_BATCH_CONCURRENCY) {
    const round = batches.slice(i, i + DEVICE_BATCH_CONCURRENCY);
    const settled = await Promise.allSettled(round.map((batch) => getDeviceBatch(batch)));
    for (let j = 0; j < settled.length; j += 1) {
      const result = settled[j];
      if (result.status === 'fulfilled') {
        result.value.forEach((device) => found.set(device.id, device));
      } else {
        failedCount += round[j].length;
      }
    }
  }

  return {
    devices: ids.filter((id) => found.has(id)).map((id) => found.get(id)),
    failedCount,
  };
}

/**
 * Whether a failed request was a 404, in either shape `get` rejects with.
 *
 * `transformError` (utils.ts) only wraps an axios error in an HTTPStatusError, which carries
 * `statusCode`, when the response body has an `error` field. `GET device/:id` answers a missing
 * device with `res.status(404).send()`, an EMPTY body, so that 404 arrives as the raw axios error
 * with the status only at `response.status`.
 */
function isNotFound(reason: unknown): boolean {
  const error = reason as { statusCode?: number; response?: { status?: number } } | undefined;
  return error?.statusCode === 404 || error?.response?.status === 404;
}

/** How many single-device lookups getDevicesOneAtATime keeps in flight. */
export const DEVICE_LOOKUP_CONCURRENCY = 10;

/**
 * {@link getDevicesInBatches} for a user who may open a device but may not search devices.
 *
 * The batch endpoint (`GET device?ids=`) requires SEARCH_DEVICE, while `GET device/:id` requires
 * only GET_DEVICE_BY_ID — which is all the device page itself asks for. Same result as the batch
 * helper: devices in the order their ids were given, a 404 is an absent device, and any other
 * failure is counted in `failedCount`. At most DEVICE_LOOKUP_CONCURRENCY requests are in flight.
 */
export async function getDevicesOneAtATime(
  ids: string[],
  lookupOne: (id: string) => Promise<Device> = (id) => getDevice(id),
): Promise<DeviceBatchLookup> {
  const found = new Map<string, Device>();
  let failedCount = 0;
  for (let i = 0; i < ids.length; i += DEVICE_LOOKUP_CONCURRENCY) {
    const round = ids.slice(i, i + DEVICE_LOOKUP_CONCURRENCY);
    const settled = await Promise.allSettled(round.map((id) => lookupOne(id)));
    for (let j = 0; j < settled.length; j += 1) {
      const result = settled[j];
      if (result.status === 'fulfilled') {
        found.set(round[j], result.value);
      } else if (!isNotFound(result.reason)) {
        failedCount += 1;
      }
    }
  }

  return {
    devices: ids.filter((id) => found.has(id)).map((id) => found.get(id)),
    failedCount,
  };
}

/** The most ids `GET device/last-seen` accepts in one call (api.tetherme.io; 101 is a 400). */
export const LAST_SEEN_BATCH_SIZE = 100;

export interface DeviceLastSeen {
  deviceId: string;
  /** Timestamp of the device's latest sample, or null when it has never reported. */
  lastSeenAt: string | null;
}

export interface DevicesLastSeenResponse {
  items: DeviceLastSeen[];
  /** Ids the server could not look up this time. Unknown, not offline. */
  unprocessedDeviceIds: string[];
}

/**
 * Bulk last-seen for up to {@link LAST_SEEN_BATCH_SIZE} devices.
 *
 * The query string is built by hand, like {@link getDeviceBatch}, so `ids` repeats as
 * `ids=a&ids=b` rather than axios's default `ids[]=a` spelling.
 *
 * Until the API that serves this route is deployed, `device/last-seen` is matched by
 * `device/:id` and answered 400. There is no 404 to detect it by.
 */
export async function getDevicesLastSeen(ids: string[]): Promise<DevicesLastSeenResponse> {
  if (ids.length === 0) {
    return { items: [], unprocessedDeviceIds: [] };
  }
  if (ids.length > LAST_SEEN_BATCH_SIZE) {
    throw new Error(`getDevicesLastSeen: ${ids.length} ids requested; the endpoint accepts at most ${LAST_SEEN_BATCH_SIZE}`);
  }

  const qs = ids.map((id) => `ids=${encodeURIComponent(id)}`).join('&');
  const response = await get<DevicesLastSeenResponse>(`device/last-seen?${qs}`);

  return {
    items: response?.items ?? [],
    unprocessedDeviceIds: response?.unprocessedDeviceIds ?? [],
  };
}

export type AssignmentInfo = {
  roomLocation?: { id: string; name?: string };
  outside?: { id: string; name?: string };
  circuit?: { id: string; name?: string };
  property?: { id: string; address1: string; addressCity: string; organisationId: string; timezone?: string };
  organisation?: { id: string; name: string; customerId: string };
  customer?: { id: string; name: string };
};

export async function getDeviceAssignmentInfo(deviceId: string): Promise<AssignmentInfo | null> {
  try {
    return await get<AssignmentInfo>(`device/${deviceId}/assignment-info`);
  } catch {
    return null;
  }
}
